import { useCallback, useEffect, useRef, useState } from "react";
import useApi from "../../hooks/useApi";
import { useToaster } from "../../hooks/useToaster";
import { SEVERITIES } from "../../data/constants";
import {
  archiveWorkflowDefinition,
  duplicateWorkflowDefinition,
  getWorkflowDefinitionGraph,
  publishWorkflowDefinition,
  saveWorkflowDefinition,
  saveWorkflowSettings,
  validateWorkflowDefinition,
} from "../../services/workflowDefinitionService";
import {
  WorkflowDefinitionGraphDto,
  WorkflowSaveRequestDto,
  WorkflowSaveResultDto,
  WorkflowValidateResultDto,
  WorkflowValidationIssueDto,
} from "../../types/workflowDefinitionDto";
import { WorkflowCanvas } from "./workflowBuilderData";
import { useActiveUsers } from "./useActiveUsers";
import { seedOnlyMessage as seedOnlyMessageFor } from "./workflowSeedOnly";
import {
  applySaveResult,
  builderToSaveRequest,
  createEmptyGraphKeys,
  forgetPersistedIds,
  graphToBuilder,
  WorkflowBuilderGraph,
  WorkflowSaveHeader,
} from "./workflowGraphMapper";

/** "new" — opened without a definition, so the Builder starts from the Form + Finalize skeleton. */
export type WorkflowBuilderLoadStatus = "new" | "loading" | "ready" | "error";

export interface WorkflowBuilderSaved {
  request: WorkflowSaveRequestDto;
  result: WorkflowSaveResultDto;
  /** True when this save created the draft (it had no id before). */
  created: boolean;
}

/** `blocked`: the server refused — 409 NOT_VALIDATED / stale, or 422 NOT_PUBLISHABLE with issues. */
export type WorkflowPublishOutcome =
  | { kind: "published" }
  | { kind: "blocked"; issues: WorkflowValidationIssueDto[] }
  /** 409 — the definition moved on (stale rowVersionTag); the page offers Reload. */
  | { kind: "stale" };

/** The wrench's fields — POST /definition/settings, separate from the graph. */
export interface WorkflowSettingsValues {
  name: string;
  description: string | null;
  category: string | null;
  smeUserId: number | null;
  redirectUrl: string | null;
}

export interface WorkflowNewVersion {
  definitionId: number;
  versionNo: number;
  /** False when the copy was made but the unsaved edits could not be saved into it. */
  editsCarried: boolean;
}

export const NEW_WORKFLOW_CATEGORY = "General";

/** Epic 80429 → AC7. The 409 names the holder by user id; their name comes from the active users. */
export const workflowLockedMessage = (holderName?: string) =>
  `This workflow is being edited by ${holderName || "another user"}. Opened read-only.`;

export const WORKFLOW_SAVE_MESSAGES = {
  // The fallback when the lock holder isn't in the active-user list (see workflowLockedMessage).
  LOCKED: workflowLockedMessage(),
  // Epic 80429 → UI: a stale-version 409 on save or publish. Shown as a banner with a Reload button.
  STALE: "Someone else changed this workflow. Reload to see the latest.",
} as const;

const headerFor = (definition: WorkflowDefinitionGraphDto | null, name: string): WorkflowSaveHeader =>
  definition
    ? {
        id: definition.id,
        name,
        token: definition.token,
        description: definition.description,
        moduleId: definition.moduleId,
        category: definition.category,
        titleFormula: definition.titleFormula,
        ownerRule: definition.ownerRule,
        rowVersionTag: definition.rowVersionTag,
      }
    : {
        id: 0,
        name,
        token: null,
        description: null,
        moduleId: null,
        category: NEW_WORKFLOW_CATEGORY,
        titleFormula: null,
        ownerRule: null,
        rowVersionTag: null,
      };

interface UseWorkflowBuilderGraphOptions {
  /** From `?definitionId=`; 0 / NaN means a new workflow. */
  definitionId: number;
  /** Receives a freshly loaded graph so the page can seed its editable state from it. */
  onLoaded: (graph: WorkflowBuilderGraph) => void;
}

/**
 * Loads a workflow definition graph for the Builder and drives its 80372 lifecycle calls: the
 * whole-graph save, validate and publish. Owns the definition header (version, status,
 * rowVersionTag, IsValidated) and the row ids the next save needs; the canvases themselves stay in
 * the page's state, where they are edited.
 */
export const useWorkflowBuilderGraph = ({ definitionId, onLoaded }: UseWorkflowBuilderGraphOptions) => {
  const { executeApi } = useApi();
  const showToaster = useToaster();
  const [definition, setDefinitionState] = useState<WorkflowDefinitionGraphDto | null>(null);
  const [status, setStatus] = useState<WorkflowBuilderLoadStatus>(definitionId ? "loading" : "new");
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // The wrench's calls: settings, archive/restore, save as new version.
  const [updating, setUpdating] = useState(false);
  // Set by a save refused because another user holds the draft lock (their user id); the page goes
  // read-only and names them.
  const [lockedByUserId, setLockedByUserId] = useState<number | null>(null);
  const lockedByAnotherUser = lockedByUserId !== null;
  // Fetched only once a lock is hit (and usually cached already by the Builder's person pickers).
  const { users: activeUsers, loading: activeUsersLoading } = useActiveUsers(lockedByAnotherUser);
  const lockHolderName = lockedByAnotherUser
    ? activeUsers.find((user) => user.userId === lockedByUserId)?.userName
    : undefined;
  const lockedMessage = workflowLockedMessage(lockHolderName);
  const lockToastShownRef = useRef(false);
  // Epic 80431: why this workflow opens read-only even for a Builder editor (the PIF, or an approver
  // option the Builder cannot represent) - null when it may be edited. A ref too, so the save paths
  // can refuse without waiting for a render.
  const [seedOnlyMessage, setSeedOnlyMessage] = useState<string | null>(null);
  const seedOnlyRef = useRef<string | null>(null);
  // Set by any stale-rowVersionTag 409; cleared by a successful (re)load.
  const [staleConflict, setStaleConflict] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const keysRef = useRef(createEmptyGraphKeys());
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  // The calls chain within one handler (Validate saves first, then validates), so each must see the
  // header the previous one just wrote — React state would still hold the pre-save value.
  const definitionRef = useRef<WorkflowDefinitionGraphDto | null>(null);
  // The definition already on screen. A new draft's first save puts its id into the URL, and that
  // must not reload — and so discard — the canvases the user is still looking at.
  const heldIdRef = useRef<number | null>(null);

  // One toast per lock, once the holder's name lookup has settled (found or not).
  useEffect(() => {
    if (!lockedByAnotherUser || activeUsersLoading || lockToastShownRef.current) return;
    lockToastShownRef.current = true;
    showToaster(lockedMessage, SEVERITIES.WARNING);
  }, [lockedByAnotherUser, activeUsersLoading, lockedMessage, showToaster]);

  const setDefinition = useCallback((next: WorkflowDefinitionGraphDto) => {
    definitionRef.current = next;
    setDefinitionState(next);
  }, []);

  useEffect(() => {
    if (!definitionId || heldIdRef.current === definitionId) return undefined;
    let active = true;
    setStatus("loading");

    // useApi alerts the failure and resolves null; the page shows the Retry card.
    executeApi(getWorkflowDefinitionGraph, definitionId).then((graph) => {
      if (!active) return;
      if (!graph) {
        setStatus("error");
        return;
      }
      const loaded = graphToBuilder(graph);
      seedOnlyRef.current = seedOnlyMessageFor(graph);
      setSeedOnlyMessage(seedOnlyRef.current);
      heldIdRef.current = loaded.definition.id;
      setStaleConflict(false);
      keysRef.current = loaded.keys;
      setDefinition(loaded.definition);
      onLoadedRef.current(loaded);
      setStatus("ready");
    });

    return () => {
      active = false;
    };
  }, [definitionId, executeApi, retryCount, setDefinition]);

  const retry = useCallback(() => setRetryCount((count) => count + 1), []);

  /** Re-reads the definition from the server, replacing whatever is on screen (unsaved edits too). */
  const reload = useCallback(() => {
    heldIdRef.current = null;
    setRetryCount((count) => count + 1);
  }, []);

  const save = useCallback(
    async (name: string, canvases: WorkflowCanvas[]): Promise<WorkflowBuilderSaved | null> => {
      // Backstop for the read-only page: never write a seed-only graph from the Builder.
      if (seedOnlyRef.current) {
        showToaster(seedOnlyRef.current, SEVERITIES.ERROR);
        return null;
      }
      const current = definitionRef.current;
      const header = headerFor(current, name);
      const request = builderToSaveRequest(header, canvases, keysRef.current);

      setSaving(true);
      const outcome = await executeApi(saveWorkflowDefinition, request);
      setSaving(false);

      // null: useApi already alerted. Either way the edits stay on screen for a retry.
      if (!outcome) return null;
      if (outcome.kind === "conflict") {
        if (outcome.data.lockedByUserId) {
          // The toast waits for the name lookup (the effect below).
          setLockedByUserId(outcome.data.lockedByUserId);
        } else {
          setStaleConflict(true);
        }
        return null;
      }
      if (outcome.kind === "invalid") {
        showToaster(outcome.issues.map((issue) => issue.message).join(" "), SEVERITIES.ERROR);
        return null;
      }

      const result = outcome.data;
      heldIdRef.current = result.definitionId;
      keysRef.current = applySaveResult([], request, result).keys;
      setDefinition({
        smeUserId: null,
        redirectUrl: null,
        ...current,
        token: header.token,
        description: header.description,
        moduleId: header.moduleId,
        category: header.category,
        titleFormula: header.titleFormula,
        ownerRule: header.ownerRule,
        name,
        id: result.definitionId,
        rootDefinitionId: result.rootDefinitionId,
        versionNo: result.versionNo,
        status: result.status,
        rowVersionTag: result.rowVersionTag,
        // Every save clears IsValidated server-side; Publish re-locks until Validate runs again.
        isValidated: false,
      });
      return { request, result, created: header.id === 0 };
    },
    [executeApi, setDefinition, showToaster],
  );

  /** Runs the server checklist against the SAVED draft — the caller saves unsaved edits first. */
  const validate = useCallback(async (): Promise<WorkflowValidateResultDto | null> => {
    const current = definitionRef.current;
    if (!current) return null;

    setValidating(true);
    const outcome = await executeApi(validateWorkflowDefinition, {
      definitionId: current.id,
      rowVersionTag: current.rowVersionTag,
    });
    setValidating(false);

    if (!outcome) return null;
    // Validate answers a failed checklist with a 200; only a stale rowVersionTag gets a 409.
    if (outcome.kind !== "ok") {
      setStaleConflict(true);
      return null;
    }
    setDefinition({
      ...current,
      isValidated: outcome.data.isValidated,
      rowVersionTag: outcome.data.rowVersionTag || current.rowVersionTag,
    });
    return outcome.data;
  }, [executeApi, setDefinition]);

  const publish = useCallback(async (): Promise<WorkflowPublishOutcome | null> => {
    const current = definitionRef.current;
    if (!current) return null;

    setPublishing(true);
    const outcome = await executeApi(publishWorkflowDefinition, {
      definitionId: current.id,
      rowVersionTag: current.rowVersionTag,
    });
    if (!outcome) {
      setPublishing(false);
      return null;
    }
    if (outcome.kind === "conflict") {
      // Publish is only offered once Validate passed, so its 409 is the stale-tag one.
      setPublishing(false);
      setStaleConflict(true);
      return { kind: "stale" };
    }
    if (outcome.kind !== "ok") {
      setPublishing(false);
      setDefinition({ ...current, isValidated: false });
      return { kind: "blocked", issues: outcome.issues };
    }

    // Publish answers without a rowVersionTag, and the wrench's settings/archive calls need a
    // current one — so the header is re-read. If that read fails, the publish still happened.
    const graph = await executeApi(getWorkflowDefinitionGraph, current.id);
    setDefinition(
      graph?.definition ?? {
        ...current,
        status: outcome.data.status,
        versionNo: outcome.data.versionNo,
      },
    );
    setPublishing(false);
    return { kind: "published" };
  }, [executeApi, setDefinition]);

  /** Settings are editable on any status and never touch the graph or IsValidated. */
  const saveSettings = useCallback(
    async (values: WorkflowSettingsValues): Promise<boolean> => {
      const current = definitionRef.current;
      if (!current) return false;

      setUpdating(true);
      const outcome = await executeApi(saveWorkflowSettings, {
        definitionId: current.id,
        rowVersionTag: current.rowVersionTag,
        ...values,
      });
      setUpdating(false);

      if (!outcome) return false;
      if (outcome.kind !== "ok") {
        setStaleConflict(true);
        return false;
      }
      setDefinition({ ...current, ...values, rowVersionTag: outcome.data.rowVersionTag });
      return true;
    },
    [executeApi, setDefinition],
  );

  /** Archives this version, or restores an archived one to draft. Resolves the new status. */
  const toggleArchive = useCallback(async (): Promise<WorkflowDefinitionGraphDto["status"] | null> => {
    const current = definitionRef.current;
    if (!current) return null;

    setUpdating(true);
    const outcome = await executeApi(archiveWorkflowDefinition, {
      definitionId: current.id,
      action: current.status === "archived" ? "restore" : "archive",
      rowVersionTag: current.rowVersionTag,
    });
    setUpdating(false);

    if (!outcome) return null;
    if (outcome.kind === "conflict") {
      setStaleConflict(true);
      return null;
    }
    if (outcome.kind === "invalid") {
      // RESTORE_BLOCKED: a newer published version exists in this lineage.
      showToaster(outcome.issues.map((issue) => issue.message).join(" "), SEVERITIES.ERROR);
      return null;
    }
    setDefinition({
      ...current,
      status: outcome.data.status,
      rowVersionTag: outcome.data.rowVersionTag,
    });
    return outcome.data.status;
  }, [executeApi, setDefinition, showToaster]);

  /**
   * duplicate mode=newVersion: the server copies the SAVED definition into a new draft at the next
   * version in the same lineage, leaving this one untouched. Unsaved edits on screen are then saved
   * into that copy as new rows, so the new version is what the user is looking at.
   */
  const saveAsNewVersion = useCallback(
    async (
      name: string,
      canvases: WorkflowCanvas[],
      carryEdits: boolean,
    ): Promise<WorkflowNewVersion | null> => {
      const current = definitionRef.current;
      if (!current) return null;
      if (seedOnlyRef.current) {
        showToaster(seedOnlyRef.current, SEVERITIES.ERROR);
        return null;
      }

      setUpdating(true);
      const outcome = await executeApi(duplicateWorkflowDefinition, {
        sourceDefinitionId: current.id,
        mode: "newVersion",
      });
      if (!outcome || outcome.kind !== "ok") {
        setUpdating(false);
        if (outcome?.kind === "invalid") {
          showToaster(outcome.issues.map((issue) => issue.message).join(" "), SEVERITIES.ERROR);
        }
        return null;
      }

      const copy = outcome.data;
      let editsCarried = !carryEdits;
      if (carryEdits) {
        // The duplicate result has no rowVersionTag, and the save needs the copy's own.
        const graph = await executeApi(getWorkflowDefinitionGraph, copy.newDefinitionId);
        if (graph) {
          const request = builderToSaveRequest(
            headerFor(graph.definition, name),
            forgetPersistedIds(canvases),
            createEmptyGraphKeys(),
          );
          const saved = await executeApi(saveWorkflowDefinition, request);
          editsCarried = saved?.kind === "ok";
        }
      }
      setUpdating(false);
      return { definitionId: copy.newDefinitionId, versionNo: copy.versionNo, editsCarried };
    },
    [executeApi, showToaster],
  );

  return {
    definition,
    status,
    saving,
    validating,
    publishing,
    updating,
    lockedByAnotherUser,
    /** "This workflow is being edited by {name}. Opened read-only." (generic if the name is unknown). */
    lockedMessage,
    /** Epic 80431: set when the workflow must not be edited in the Builder (the PIF); the reason to show. */
    seedOnlyMessage,
    staleConflict,
    retry,
    reload,
    save,
    validate,
    publish,
    saveSettings,
    toggleArchive,
    saveAsNewVersion,
  };
};
